import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Artifact } from '@/api/configuration';
import { createArtifact, deleteArtifact, updateArtifact, updateArtifactPartial } from '@/api/configuration';
import { useArtifacts, useInvalidateConfiguration, useServiceDefinitions } from '@/hooks/use-configuration';
import { useToastStore } from '@/stores/toast-store';

// --- Default Fields Validation ---

interface DefaultFieldValues {
  defaultDownloadPath: string;
  defaultDeployPath: string;
  defaultPort: string;
  defaultOwnerGroup: string;
  defaultOwnerName: string;
  defaultServiceDefinitionId: string;
  defaultContextPath: string;
  defaultSdkVersion: string;
}

interface ValidationErrors {
  defaultDownloadPath?: string;
  defaultDeployPath?: string;
  defaultPort?: string;
  defaultOwnerGroup?: string;
  defaultOwnerName?: string;
  defaultServiceDefinitionId?: string;
  defaultContextPath?: string;
  defaultSdkVersion?: string;
}

function validateDefaultFields(values: DefaultFieldValues): ValidationErrors {
  const errors: ValidationErrors = {};
  const textFields: (keyof DefaultFieldValues)[] = [
    'defaultDownloadPath',
    'defaultDeployPath',
    'defaultOwnerGroup',
    'defaultOwnerName',
    'defaultContextPath',
    'defaultSdkVersion',
  ];

  for (const field of textFields) {
    if (values[field].length > 255) {
      errors[field] = 'Must not exceed 255 characters';
    }
  }

  if (values.defaultPort.trim() !== '') {
    const portNum = Number(values.defaultPort);
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      errors.defaultPort = 'Port must be a number between 1 and 65535';
    }
  }

  return errors;
}

// --- Default Fields Display & Edit Form ---

function ArtifactDefaultFields({
  artifact,
  projectKey,
  onUpdated,
}: {
  artifact: Artifact;
  projectKey: string;
  onUpdated: () => Promise<void>;
}) {
  const { data: serviceDefinitions = [] } = useServiceDefinitions(projectKey);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const [formValues, setFormValues] = useState<DefaultFieldValues>(() => ({
    defaultDownloadPath: artifact.defaultDownloadPath ?? '',
    defaultDeployPath: artifact.defaultDeployPath ?? '',
    defaultPort: artifact.defaultPort != null ? String(artifact.defaultPort) : '',
    defaultOwnerGroup: artifact.defaultOwnerGroup ?? '',
    defaultOwnerName: artifact.defaultOwnerName ?? '',
    defaultServiceDefinitionId: artifact.defaultServiceDefinitionId ?? '',
    defaultContextPath: artifact.defaultContextPath ?? '',
    defaultSdkVersion: artifact.defaultSdkVersion ?? '',
  }));

  const hasAnyDefault =
    artifact.defaultDownloadPath ||
    artifact.defaultDeployPath ||
    artifact.defaultPort != null ||
    artifact.defaultOwnerGroup ||
    artifact.defaultOwnerName ||
    artifact.defaultServiceDefinitionId ||
    artifact.defaultContextPath ||
    artifact.defaultSdkVersion;

  const handleEditClick = () => {
    setFormValues({
      defaultDownloadPath: artifact.defaultDownloadPath ?? '',
      defaultDeployPath: artifact.defaultDeployPath ?? '',
      defaultPort: artifact.defaultPort != null ? String(artifact.defaultPort) : '',
      defaultOwnerGroup: artifact.defaultOwnerGroup ?? '',
      defaultOwnerName: artifact.defaultOwnerName ?? '',
      defaultServiceDefinitionId: artifact.defaultServiceDefinitionId ?? '',
      defaultContextPath: artifact.defaultContextPath ?? '',
      defaultSdkVersion: artifact.defaultSdkVersion ?? '',
    });
    setErrors({});
    setSaveError(null);
    setEditing(true);
    setExpanded(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setErrors({});
    setSaveError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateDefaultFields(formValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSaveError(null);
    setSaving(true);
    try {
      await updateArtifactPartial(projectKey, artifact.id, {
        defaultDownloadPath: formValues.defaultDownloadPath || undefined,
        defaultDeployPath: formValues.defaultDeployPath || undefined,
        defaultPort: formValues.defaultPort.trim() !== '' ? Number(formValues.defaultPort) : undefined,
        defaultOwnerGroup: formValues.defaultOwnerGroup || undefined,
        defaultOwnerName: formValues.defaultOwnerName || undefined,
        defaultServiceDefinitionId: formValues.defaultServiceDefinitionId || undefined,
        defaultContextPath: formValues.defaultContextPath || undefined,
        defaultSdkVersion: formValues.defaultSdkVersion || undefined,
      });
      await onUpdated();
      setEditing(false);
      setSaveError(null);
    } catch {
      setSaveError('Failed to save default fields. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof DefaultFieldValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    // Clear error on edit
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <div className="mt-2 border-t border-border pt-2">
      {/* Collapsible header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium text-content-secondary hover:text-content transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 rounded px-1 py-0.5"
          aria-expanded={expanded}
        >
          <svg
            className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Deployment
          {!hasAnyDefault && !editing && (
            <span className="text-content-tertiary font-normal">(none set)</span>
          )}
        </button>
        {!editing && (
          <button
            type="button"
            onClick={handleEditClick}
            className="rounded px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
          >
            Edit Defaults
          </button>
        )}
      </div>

      {/* Expanded content */}
      {expanded && !editing && (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs pl-5">
          <DefaultFieldDisplay label="Download Path" value={artifact.defaultDownloadPath} />
          <DefaultFieldDisplay label="Deploy Path" value={artifact.defaultDeployPath} />
          <DefaultFieldDisplay label="Port" value={artifact.defaultPort != null ? String(artifact.defaultPort) : undefined} />
          <DefaultFieldDisplay label="Owner Group" value={artifact.defaultOwnerGroup} />
          <DefaultFieldDisplay label="Owner Name" value={artifact.defaultOwnerName} />
          <DefaultFieldDisplay label="Context Path" value={artifact.defaultContextPath} />
          <DefaultFieldDisplay label="SDK Version" value={artifact.defaultSdkVersion} />
          <DefaultFieldDisplay label="Service Definition" value={serviceDefinitions.find((sd) => sd.id === artifact.defaultServiceDefinitionId)?.name ?? artifact.defaultServiceDefinitionId} />
        </div>
      )}

      {/* Edit form */}
      {expanded && editing && (
        <form onSubmit={handleSave} className="mt-2 pl-5 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <DefaultFieldInput
              id={`${artifact.id}-download-path`}
              label="Download Path"
              value={formValues.defaultDownloadPath}
              onChange={(v) => updateField('defaultDownloadPath', v)}
              error={errors.defaultDownloadPath}
              placeholder="/path/to/download"
            />
            <DefaultFieldInput
              id={`${artifact.id}-deploy-path`}
              label="Deploy Path"
              value={formValues.defaultDeployPath}
              onChange={(v) => updateField('defaultDeployPath', v)}
              error={errors.defaultDeployPath}
              placeholder="/opt/apps/my-app"
            />
            <DefaultFieldInput
              id={`${artifact.id}-port`}
              label="Port"
              value={formValues.defaultPort}
              onChange={(v) => updateField('defaultPort', v)}
              error={errors.defaultPort}
              placeholder="8080"
              type="text"
              inputMode="numeric"
            />
            <DefaultFieldInput
              id={`${artifact.id}-owner-group`}
              label="Owner Group"
              value={formValues.defaultOwnerGroup}
              onChange={(v) => updateField('defaultOwnerGroup', v)}
              error={errors.defaultOwnerGroup}
              placeholder="appuser"
            />
            <DefaultFieldInput
              id={`${artifact.id}-owner-name`}
              label="Owner Name"
              value={formValues.defaultOwnerName}
              onChange={(v) => updateField('defaultOwnerName', v)}
              error={errors.defaultOwnerName}
              placeholder="appuser"
            />
            <DefaultFieldInput
              id={`${artifact.id}-context-path`}
              label="Context Path"
              value={formValues.defaultContextPath}
              onChange={(v) => updateField('defaultContextPath', v)}
              error={errors.defaultContextPath}
              placeholder="my-app"
            />
            <DefaultFieldInput
              id={`${artifact.id}-sdk-version`}
              label="SDK Version"
              value={formValues.defaultSdkVersion}
              onChange={(v) => updateField('defaultSdkVersion', v)}
              error={errors.defaultSdkVersion}
              placeholder="3.12 (Python), 21 (JDK)..."
            />
            <div>
              <label htmlFor={`${artifact.id}-svc-def`} className="block text-xs font-medium text-content-secondary mb-1">
                Service Definition
              </label>
              <select
                id={`${artifact.id}-svc-def`}
                value={formValues.defaultServiceDefinitionId}
                onChange={(e) => updateField('defaultServiceDefinitionId', e.target.value)}
                className={`w-full rounded-md border px-3 py-1.5 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                  errors.defaultServiceDefinitionId ? 'border-red-500 dark:border-red-400' : 'border-border'
                }`}
                aria-invalid={!!errors.defaultServiceDefinitionId}
                aria-describedby={errors.defaultServiceDefinitionId ? `${artifact.id}-svc-def-error` : undefined}
              >
                <option value="">— None —</option>
                {serviceDefinitions.map((sd) => (
                  <option key={sd.id} value={sd.id}>{sd.name}{sd.type ? ` (${sd.type})` : ''}</option>
                ))}
              </select>
              {errors.defaultServiceDefinitionId && (
                <p id={`${artifact.id}-svc-def-error`} className="mt-0.5 text-xs text-red-600 dark:text-red-400" role="alert">
                  {errors.defaultServiceDefinitionId}
                </p>
              )}
            </div>
          </div>

          {saveError && (
            <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-1.5">
              <p className="text-xs text-red-700 dark:text-red-300">{saveError}</p>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving && (
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function DefaultFieldDisplay({ label, value }: { label: string; value?: string }) {
  return (
    <div className="min-w-0">
      <span className="text-content-tertiary">{label}:</span>{' '}
      <span className={value ? 'text-content' : 'text-content-tertiary italic'}>
        {value || '—'}
      </span>
    </div>
  );
}

function DefaultFieldInput({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  type = 'text',
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
  inputMode?: 'numeric' | 'text';
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-content-secondary mb-1">
        {label}
      </label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-md border px-3 py-1.5 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 ${
          error ? 'border-red-500 dark:border-red-400' : 'border-border'
        }`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <p id={`${id}-error`} className="mt-0.5 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// --- Shared UI helpers ---

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-content-tertiary">{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// --- Deployable Toggle ---

function DeployableToggle({
  artifact,
  projectKey,
  onUpdated,
}: {
  artifact: Artifact;
  projectKey: string;
  onUpdated: () => Promise<void>;
}) {
  const [optimisticValue, setOptimisticValue] = useState<boolean | null>(null);
  const [updating, setUpdating] = useState(false);
  const addToast = useToastStore((state) => state.addToast);

  const checked = optimisticValue ?? artifact.deployable;

  const handleToggle = async () => {
    if (updating) return;
    const newValue = !checked;
    setOptimisticValue(newValue);
    setUpdating(true);
    try {
      await updateArtifactPartial(projectKey, artifact.id, { deployable: newValue });
      await onUpdated();
      setOptimisticValue(null);
    } catch {
      // Revert to previous state
      setOptimisticValue(null);
      addToast({ type: 'error', message: `Failed to update deployable status for "${artifact.name}".`, duration: 8000 });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`Mark ${artifact.name} as ${checked ? 'not deployable' : 'deployable'}`}
      disabled={updating}
      onClick={handleToggle}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
        updating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-brand-700' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-[20px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  );
}

// --- Artifact Form ---

function ArtifactForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Artifact;
  onSave: (data: Omit<Artifact, 'id' | 'projectKey'>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [artifactRepository, setArtifactRepository] = useState(initial?.artifactRepository ?? '');
  const [artifactGroup, setArtifactGroup] = useState(initial?.artifactGroup ?? '');
  const [artifactName, setArtifactName] = useState(initial?.artifactName ?? '');
  const [artifactPackaging, setArtifactPackaging] = useState(initial?.artifactPackaging ?? '');
  const [downloadType, setDownloadType] = useState<'maven' | 'npm' | 'pypi' | ''>(initial?.downloadType ?? '');
  const [defaultDeploymentType, setDefaultDeploymentType] = useState(initial?.defaultDeploymentType ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      deployable: initial?.deployable ?? false,
      artifactRepository: artifactRepository || undefined,
      artifactGroup: artifactGroup || undefined,
      artifactName: artifactName || undefined,
      artifactPackaging: artifactPackaging || undefined,
      downloadType: downloadType || undefined,
      defaultDeploymentType: defaultDeploymentType || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-3">
      <p className="text-xs font-semibold text-content-secondary">
        {initial ? 'Edit Artifact' : 'New Artifact Definition'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="sm:col-span-2 lg:col-span-3">
          <label htmlFor="art-name" className="block text-xs font-medium text-content-secondary mb-1">Display Name *</label>
          <input id="art-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="my-application" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="art-repository" className="block text-xs font-medium text-content-secondary mb-1">Artifact Repository</label>
          <input id="art-repository" type="text" value={artifactRepository} onChange={(e) => setArtifactRepository(e.target.value)} placeholder="maven-releases, npm-all..." className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="art-group" className="block text-xs font-medium text-content-secondary mb-1">Artifact Group</label>
          <input id="art-group" type="text" value={artifactGroup} onChange={(e) => setArtifactGroup(e.target.value)} placeholder="com.myorg.apps" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="art-artifact-name" className="block text-xs font-medium text-content-secondary mb-1">Artifact Name</label>
          <input id="art-artifact-name" type="text" value={artifactName} onChange={(e) => setArtifactName(e.target.value)} placeholder="my-app-api" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="art-packaging" className="block text-xs font-medium text-content-secondary mb-1">Packaging</label>
          <input id="art-packaging" type="text" value={artifactPackaging} onChange={(e) => setArtifactPackaging(e.target.value)} placeholder="jar, war, zip..." className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
        </div>
        <div>
          <label htmlFor="art-download-type" className="block text-xs font-medium text-content-secondary mb-1">Download Type</label>
          <select id="art-download-type" value={downloadType} onChange={(e) => setDownloadType(e.target.value as 'maven' | 'npm' | 'pypi' | '')} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
            <option value="">— None —</option>
            <option value="maven">Maven</option>
            <option value="npm">npm</option>
            <option value="pypi">PyPI</option>
          </select>
        </div>
        <div>
          <label htmlFor="art-deployment-type" className="block text-xs font-medium text-content-secondary mb-1">Deployment Type</label>
          <select id="art-deployment-type" value={defaultDeploymentType} onChange={(e) => setDefaultDeploymentType(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
            <option value="">— None —</option>
            <option value="SPRING">Spring Boot</option>
            <option value="TOMCAT">Tomcat</option>
            <option value="WEBAPP">Web Application</option>
            <option value="JBOSS">JBoss</option>
            <option value="OSGI">OSGi</option>
            <option value="NODE">Node.js</option>
            <option value="PYTHON">Python</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          {initial ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Main Page Component ---

export function ProjectArtifactsPage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';

  const { data: artifacts = [], isLoading, error: fetchError } = useArtifacts(key);
  const { invalidateArtifacts } = useInvalidateConfiguration(key);

  const [showForm, setShowForm] = useState(false);
  const [editingArtifact, setEditingArtifact] = useState<Artifact | null>(null);

  const sortedArtifacts = useMemo(
    () => [...artifacts].sort((a, b) => a.name.localeCompare(b.name)),
    [artifacts]
  );

  const handleCreate = useCallback(async (data: Omit<Artifact, 'id' | 'projectKey'>) => {
    try {
      await createArtifact(key, data);
      await invalidateArtifacts();
      setShowForm(false);
    } catch {
      // form stays open for retry
    }
  }, [key, invalidateArtifacts]);

  const handleUpdate = useCallback(async (data: Omit<Artifact, 'id' | 'projectKey'>) => {
    if (!editingArtifact) return;
    try {
      await updateArtifact(key, editingArtifact.id, data);
      await invalidateArtifacts();
      setEditingArtifact(null);
    } catch {
      // form stays open for retry
    }
  }, [key, editingArtifact, invalidateArtifacts]);

  const handleDelete = useCallback(async (artifact: Artifact) => {
    if (!confirm(`Delete artifact "${artifact.name}"? This cannot be undone.`)) return;
    try {
      await deleteArtifact(key, artifact.id);
      await invalidateArtifacts();
    } catch {
      // silently fail
    }
  }, [key, invalidateArtifacts]);

  const error = fetchError ? 'Failed to load artifacts.' : null;

  return (
    <main id="main-content" className="mx-auto max-w-5xl">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Artifacts</h1>
          <p className="mt-1 text-sm text-content-secondary">
            Manage artifact definitions for this project
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setShowForm(true); setEditingArtifact(null); }}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 transition-colors"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Artifact
        </button>
      </header>

      {/* Create form */}
      {showForm && !editingArtifact && (
        <div className="mb-4">
          <ArtifactForm
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Loading / Error / List */}
      {isLoading && <LoadingState />}
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {!isLoading && !error && sortedArtifacts.length === 0 && !showForm && (
        <EmptyState message="No artifacts defined yet. Add one to get started." />
      )}

      {!isLoading && sortedArtifacts.length > 0 && (
        <ul className="space-y-2">
          {sortedArtifacts.map((art) =>
            editingArtifact?.id === art.id ? (
              <li key={art.id}>
                <ArtifactForm
                  initial={art}
                  onSave={handleUpdate}
                  onCancel={() => setEditingArtifact(null)}
                />
              </li>
            ) : (
              <li key={art.id} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <DeployableToggle artifact={art} projectKey={key} onUpdated={invalidateArtifacts} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-content">{art.name}</p>
                        <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-content-secondary">
                          {art.downloadType && (
                            <span className="inline-flex items-center rounded-full bg-brand-100 dark:bg-brand-900/30 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                              {art.downloadType}
                            </span>
                          )}
                          {art.artifactRepository && <span>Repo: {art.artifactRepository}</span>}
                          {art.artifactGroup && <span>Group: {art.artifactGroup}</span>}
                          {art.artifactName && <span>Artifact: {art.artifactName}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => { setEditingArtifact(art); setShowForm(false); }}
                      className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(art)}
                      className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <ArtifactDefaultFields artifact={art} projectKey={key} onUpdated={invalidateArtifacts} />
              </li>
            )
          )}
        </ul>
      )}
    </main>
  );
}
